import React, {FC, useEffect, useMemo} from 'react';
import NoData from '../../Common/NoData';
import WidgetLineups from '../../Match/WidgetLineups';
import {MatchLineups, Team, TimelineUpdate} from '../../../utils/types';
import useMatchLineups from '../../../hooks/useMatchLineups';
import deriveLineupsFromUpdates from './deriveLineupFromUpdates';
import Loader from '../../Common/Loader';
import usePolling from '../../../hooks/usePolling';
import getConfig from '../../../getConfig';

interface AsyncWidgetLineupsProps {
  matchId: string;
  updates: TimelineUpdate[];
  team: Team;
}

const {pollingRates} = getConfig();

/**
 * Wraps up the business logic of fetching the current match lineups  only when the "lineup" tab is
 * displayed (mounted into the DOM), then passes the data down to the WidgetLineups component for
 * visualization.
 */
const AsyncWidgetLineups: FC<AsyncWidgetLineupsProps> = (props: AsyncWidgetLineupsProps) => {
  const {matchId, updates, team} = props;
  const {withPolling} = usePolling();
  const [matchLineupsState, {getMatchLineupsByEventId}] = useMatchLineups();
  const lineupData: MatchLineups | undefined = useMemo(
    () => deriveLineupsFromUpdates(updates, matchLineupsState.data),
    [matchLineupsState, updates]
  );
  const repeatMatchLineupsByEventId = withPolling(
    getMatchLineupsByEventId,
    pollingRates.lineups,
    true
  );

  // when the matchId changes fetch the line-ups
  useEffect(() => {
    if (matchId) {
      repeatMatchLineupsByEventId(matchId);
    }

    return repeatMatchLineupsByEventId.cancel;
  }, [matchId, repeatMatchLineupsByEventId]);

  if (!lineupData && matchLineupsState.loading) return <Loader />;
  if (!lineupData || !lineupData.home.main.length) return <NoData />;

  return <WidgetLineups matchLineups={lineupData} team={team} />;
};

export default React.memo(AsyncWidgetLineups);
